import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { seo } from "@/lib/seo";
import { LegalPageSkeleton } from "@/components/skeletons";

export const Route = createFileRoute("/reembolso")({
  head: () =>
    seo({
      path: "/reembolso",
      title: "Política de reembolso — Seu Bolso Tranquilo",
      description:
        "Política de reembolso do Seu Bolso Tranquilo Premium: garantia de satisfação de 30 dias. Saiba como solicitar o reembolso da assinatura.",
      breadcrumbs: [
        { name: "Início", path: "/" },
        { name: "Política de reembolso", path: "/reembolso" },
      ],
    }),
  component: RefundPage,
  pendingComponent: LegalPageSkeleton,
});

function RefundPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo className="size-9" />
            <span className="font-display font-bold">Seu Bolso Tranquilo</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" /> Voltar
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Legal</p>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-3">
          Política de reembolso
        </h1>
        <p className="text-sm text-muted-foreground mb-10">Última atualização: 26 de abril de 2026</p>

        <div className="space-y-8 text-foreground/90 leading-relaxed">
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">1. Garantia de satisfação de 30 dias</h2>
            <p className="text-muted-foreground">
              Oferecemos uma garantia de reembolso integral de 30 dias para todas as assinaturas do plano Premium do
              Seu Bolso Tranquilo (mensal ou anual). Se por qualquer motivo você não estiver satisfeito com o serviço,
              pode solicitar o reembolso completo dentro de 30 dias a partir da data da cobrança.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">2. Como solicitar o reembolso</h2>
            <p className="text-muted-foreground">
              Os reembolsos são processados pelo nosso provedor de pagamentos, a Paddle, que atua como Merchant of
              Record (revendedor oficial) das nossas assinaturas. Para solicitar um reembolso, acesse{" "}
              <a href="https://paddle.net" target="_blank" rel="noopener noreferrer" className="underline text-foreground hover:text-primary-glow">
                paddle.net
              </a>{" "}
              e localize a sua compra usando o e-mail utilizado no momento da contratação, ou entre em contato com o
              nosso suporte pelo canal disponibilizado dentro do aplicativo, que encaminhará a solicitação.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">3. Prazo de processamento</h2>
            <p className="text-muted-foreground">
              Após a aprovação do reembolso pela Paddle, o valor é estornado para o mesmo método de pagamento utilizado
              na compra. O prazo para o crédito aparecer na fatura ou no extrato pode variar de 5 a 10 dias úteis,
              dependendo do banco ou operadora do cartão.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">4. Cancelamento da assinatura</h2>
            <p className="text-muted-foreground">
              Você pode cancelar a sua assinatura Premium a qualquer momento — o cancelamento interrompe as cobranças
              futuras, e o acesso aos recursos Premium permanece ativo até o final do período já pago. O cancelamento,
              por si só, não gera reembolso automático: para isso, é necessário solicitar dentro do prazo de 30 dias
              conforme descrito acima.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">5. Renovações</h2>
            <p className="text-muted-foreground">
              Cada renovação (mensal ou anual) inicia um novo prazo de 30 dias para solicitação de reembolso a partir
              da data da nova cobrança.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">6. Contato</h2>
            <p className="text-muted-foreground">
              Em caso de dúvidas sobre esta política, entre em contato pelo canal de suporte disponibilizado dentro do
              aplicativo. Para questões relacionadas diretamente à cobrança, a Paddle também oferece atendimento em{" "}
              <a href="https://paddle.net" target="_blank" rel="noopener noreferrer" className="underline text-foreground hover:text-primary-glow">
                paddle.net
              </a>
              .
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-border/40 px-5 lg:px-8 py-8 mt-10">
        <div className="max-w-4xl mx-auto text-center text-sm text-muted-foreground">
          Seu Bolso Tranquilo © 2026
        </div>
      </footer>
    </div>
  );
}
